import { cn } from "@/lib/utils";
import React, { ReactNode, useEffect, useState, useRef } from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { LoaderCircle, Square, Upload, Bot } from "lucide-react";

// Import animated icons
import { AnimateIcon } from "@/components/animate-ui/icons/icon";
import { Plus } from "@/components/animate-ui/icons/plus";
import { Send } from "@/components/animate-ui/icons/send";
import { Settings } from "@/components/animate-ui/icons/settings";
import { useChatStore } from "@/stores/chat-store";
import { useModelsQuery } from "@/api/queries/ModelQueries";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { useArtifactStore } from "@/stores/artifact-store";
import { UploadedFile } from "./FileUpload";
import FileAttachmentComponent from "../chat/FileAttachment";
import { toast } from "sonner";
import { useCreateThreadMutation } from "@/api/mutations/ThreadMutations";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Card, CardContent } from "../ui/card";
import { Separator } from "../ui/separator";
// import { useWebSearchQuery } from "@/api/queries/admin/WebSearchQueries";

type Props = {
    warningMessage?: ReactNode;
    isSending?: boolean;
    threadId?: string;
    textareaRef: React.RefObject<HTMLTextAreaElement>;
    handleInputValueChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    handleSendMessage: () => void;
    handleStopGeneration: () => void;
    attachedFiles?: UploadedFile[];
    onAttachedFilesChange?: (files: UploadedFile[]) => void;
};

const ChatInputArea = ({
    handleInputValueChange,
    handleKeyDown,
    threadId,
    handleSendMessage,
    handleStopGeneration,
    isSending,
    textareaRef,
    warningMessage,
    attachedFiles: externalAttachedFiles,
    onAttachedFilesChange,
}: Props) => {
    const { t } = useTranslation();
    const { data: models } = useModelsQuery();
    const { setModelId } = useChatStore();
    const { isOpen } = useArtifactStore();
    const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>(externalAttachedFiles || []);
    const [isDragging, setIsDragging] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ [fileName: string]: number }>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { mutateAsync: createThread } = useCreateThreadMutation();
    const navigate = useNavigate();

    // Fetch user's enabled agent connections
    const { data: userConnections, isLoading: loadingConnections } = trpc.userGroups.getMyAgentConnections.useQuery();

    // Maximum files allowed
    const MAX_FILES = 5;

    // Sync external attached files with internal state
    useEffect(() => {
        if (externalAttachedFiles) {
            setAttachedFiles(externalAttachedFiles);
        }
    }, [externalAttachedFiles]);

    // Notify parent of attached files changes
    useEffect(() => {
        onAttachedFilesChange?.(attachedFiles);
    }, [attachedFiles, onAttachedFilesChange]);

    useEffect(() => {
        if (models && models.length > 0) {
            setModelId(models[0].id);
        }
    }, [models, setModelId]);

    const handleFileRemoved = (fileId: string) => {
        const updatedFiles = attachedFiles.filter((file) => file.id !== fileId);
        setAttachedFiles(updatedFiles);
        onAttachedFilesChange?.(updatedFiles);
    };

    // Direct file upload for + button
    const handleDirectFileUpload = () => {
        if (attachedFiles.length >= MAX_FILES) {
            toast.error(t("chat.maxFilesAllowed", { max: MAX_FILES }));
            return;
        }
        fileInputRef.current?.click();
    };

    const processFileUpload = async (files: File[]) => {
        if (files.length === 0) return;

        // Check if adding these files would exceed the limit
        if (attachedFiles.length + files.length > MAX_FILES) {
            toast.error(
                t("chat.maxFilesExceeded", { max: MAX_FILES, current: attachedFiles.length })
            );
            return;
        }

        setIsUploading(true);
        let successCount = 0;
        let failCount = 0;

        // Initialize progress for all files
        const initialProgress: { [fileName: string]: number } = {};
        files.forEach((file) => {
            initialProgress[file.name] = 0;
        });
        setUploadProgress(initialProgress);

        toast.info(t("chat.uploadingFiles", { count: files.length }));

        // Process files one by one
        for (const file of files) {
            try {
                // Set progress to 10% when starting
                setUploadProgress((prev) => ({
                    ...prev,
                    [file.name]: 10,
                }));

                const formData = new FormData();

                // Determine endpoint and field name based on file type
                const isImage = file.type.startsWith("image/");
                const endpoint = isImage ? "/upload/minio/image" : "/upload/minio/file";
                const fieldName = isImage ? "image" : "file";

                formData.append(fieldName, file);
                formData.append("analyze", "true");
                if (threadId) {
                    formData.append("threadId", threadId);
                } else {
                    const response = await createThread({ title: "New Chat" });
                    if (!response) throw new Error("Thread creation failed");
                    formData.append("threadId", response.id);
                    navigate(`/chats/${response.id}`, {
                        replace: true,
                    });
                }

                // Set progress to 30% when sending request
                setUploadProgress((prev) => ({
                    ...prev,
                    [file.name]: 30,
                }));

                const response = await fetch(endpoint, {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                });

                // Set progress to 80% when response received
                setUploadProgress((prev) => ({
                    ...prev,
                    [file.name]: 80,
                }));

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.file) {
                        setAttachedFiles((prev) => {
                            const updatedFiles = [...prev, result.file];
                            onAttachedFilesChange?.(updatedFiles);
                            return updatedFiles;
                        });

                        // Set progress to 100% on success
                        setUploadProgress((prev) => ({
                            ...prev,
                            [file.name]: 100,
                        }));

                        successCount++;
                    } else {
                        throw new Error(result.error || "Upload failed");
                    }
                } else {
                    throw new Error(`Upload failed: ${response.statusText}`);
                }
            } catch (error) {
                console.error("File upload failed:", error);
                failCount++;

                // Remove failed file from progress
                setUploadProgress((prev) => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                toast.error(t("chat.uploadFailed", { fileName: file.name }));
            }
        }

        // Clear upload progress after a short delay
        setTimeout(() => {
            setUploadProgress({});
        }, 1000);

        setIsUploading(false);

        // Show final result
        if (successCount > 0) {
            const failedMsg = failCount > 0 ? `, ${t("chat.uploadFailedCount", { count: failCount })}` : "";
            toast.success(
                t("chat.uploadSuccessCount", { count: successCount }) + failedMsg
            );
        } else if (failCount > 0) {
            toast.error(t("chat.uploadFailedMultiple", { count: failCount }));
        }
    };

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        await processFileUpload(files);
        // Reset input
        e.target.value = "";
    };

    // Drag and drop handlers
    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter((prev) => prev + 1);
        if (e.dataTransfer.types.includes("Files")) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter((prev) => prev - 1);
        if (dragCounter <= 1) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setDragCounter(0);

        const files = Array.from(e.dataTransfer.files);
        await processFileUpload(files);
    };

    // Get current upload files count for display
    const currentUploads = Object.keys(uploadProgress).length;

    return (
        <div
            className={cn(
                "w-full max-w-xl pb-4 md:px-16 xl:max-w-xl 3xl:max-w-2xl 4xl:max-w-3xl",
                isOpen && "max-w-sm xl:max-w-md 3xl:max-w-xl 4xl:max-w-2xl",
                threadId && threadId != "" ? "sticky bottom-0 left-0 right-0 bg-background md:mx-0 md:px-0" : ""
            )}
        >
            <div className="flex flex-col gap-2">
                {warningMessage && warningMessage}

                {/* Upload Progress Display */}
                {isUploading && currentUploads > 0 && (
                    <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/20">
                        <div className="flex items-center gap-2">
                            <LoaderCircle className="h-4 w-4 animate-spin text-blue-600" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                {t("chat.uploadingFiles", { count: currentUploads })}
                            </span>
                        </div>
                        <div className="space-y-1">
                            {Object.entries(uploadProgress).map(([fileName, progress]) => (
                                <div key={fileName} className="space-y-1">
                                    <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400">
                                        <span className="max-w-[200px] truncate">{fileName}</span>
                                        <span>{progress}%</span>
                                    </div>
                                    <div className="h-1.5 w-full rounded-full bg-blue-200 dark:bg-blue-900">
                                        <div
                                            className="h-1.5 rounded-full bg-blue-600 transition-all duration-300 dark:bg-blue-400"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Attached Files Display */}
                {attachedFiles.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">
                                {t("chat.attachedFiles", { current: attachedFiles.length, max: MAX_FILES })}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setAttachedFiles([]);
                                    onAttachedFilesChange?.([]);
                                }}
                                className="h-6 text-xs text-red-500 hover:text-red-700"
                                disabled={isUploading}
                            >
                                {t("chat.removeAll")}
                            </Button>
                        </div>
                        <div className="grid max-h-40 gap-2 overflow-y-auto">
                            {attachedFiles.map((file) => (
                                <FileAttachmentComponent
                                    key={file.id}
                                    file={file}
                                    onRemove={handleFileRemoved}
                                    showRemoveButton={true}
                                    compact={true}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    <div
                        className={cn(
                            "relative rounded-md border pb-12 pt-1 shadow transition-all duration-200",
                            isDragging && "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20",
                            isUploading && "border-blue-300 bg-blue-50/30 dark:bg-blue-950/10"
                        )}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        {/* Drag overlay */}
                        {isDragging && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-blue-500/10 backdrop-blur-sm">
                                <div className="flex flex-col items-center gap-2 text-blue-600 dark:text-blue-400">
                                    <Upload size={32} />
                                    <div className="text-center">
                                        <p className="font-medium">{t("chat.dropFilesHere")}</p>
                                        <p className="text-sm">
                                            {t("chat.maxFilesUsed", { max: MAX_FILES, current: attachedFiles.length })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Textarea
                            ref={textareaRef}
                            onChange={handleInputValueChange}
                            onKeyDown={handleKeyDown}
                            placeholder={isDragging ? t("chat.dropFilesHereMax", { max: MAX_FILES }) : t("chat.askQuestion")}
                            rows={1}
                            className="max-h-[200px] min-h-fit flex-1 resize-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                        <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 p-1">
                            <div className="flex w-full justify-between">
                                <div className="flex items-center gap-2">
                                    {/* Direct upload button - replaces popover */}
                                    <TooltipProvider>
                                        <Tooltip delayDuration={0}>
                                            <TooltipTrigger asChild>
                                                <AnimateIcon animateOnHover>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={handleDirectFileUpload}
                                                        disabled={attachedFiles.length >= MAX_FILES}
                                                        className={cn(isUploading && "opacity-50")}
                                                    >
                                                        <Plus size={20} animation="default" />
                                                    </Button>
                                                </AnimateIcon>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {attachedFiles.length >= MAX_FILES
                                                    ? t("chat.maxFilesReached", { max: MAX_FILES })
                                                    : isUploading
                                                      ? t("chat.uploadingProgress", { current: attachedFiles.length, max: MAX_FILES })
                                                      : t("chat.uploadFiles", { current: attachedFiles.length, max: MAX_FILES })}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>

                                    {/* Hidden file input for direct upload */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        className="hidden"
                                        onChange={handleFileInputChange}
                                        accept="image/*,text/*,.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb,.ppt,.pptx,.pptm,.csv,.json,.js,.ts,.tsx,.jsx,.py,.java,.cpp,.c,.cs,.php,.rb,.go,.rs,.swift,.kt,.scala,.sh,.sql,.html,.css,.scss,.vue,.svelte,.zip,.rar,.7z,.tar,.gz,.xml,.rtf,.odt,.ods,.odp"
                                    />
                                    <TooltipProvider>
                                        <Tooltip delayDuration={0}>
                                            <TooltipTrigger asChild>
                                                <AnimateIcon animateOnHover>
                                                    <Button variant="ghost" size="icon">
                                                        <Settings size={20} animation="default" />
                                                    </Button>
                                                </AnimateIcon>
                                            </TooltipTrigger>
                                            <TooltipContent>{t("chat.settings")}</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    {/* Available Agents Indicator */}
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="ghost" size="icon">
                                                <Bot size={20} />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-80 p-0" side="bottom" align="start">
                                            <Card className="border-0 shadow-none">
                                                <CardContent className="p-0">
                                                    {loadingConnections ? (
                                                        <div className="px-4 py-3 text-sm text-muted-foreground">
                                                            {t("chat.agents.loadingAgents")}
                                                        </div>
                                                    ) : userConnections && userConnections.length > 0 ? (
                                                        <div>
                                                            <div className="px-4 py-2.5 bg-muted/30">
                                                                <span className="text-xs font-semibold text-foreground/90">
                                                                    {t("chat.agents.activeAgents")}
                                                                </span>
                                                            </div>
                                                            <Separator />
                                                            <div className="p-2">
                                                                {userConnections.map((connection: any, index: number) => (
                                                                    <div key={connection.id}>
                                                                        <div className="flex items-center justify-between gap-3 rounded-md px-3 py-2.5 hover:bg-muted/50 transition-colors">
                                                                            <span className="text-sm font-medium truncate text-foreground">
                                                                                {connection.name}
                                                                            </span>
                                                                            <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider flex-shrink-0">
                                                                                {connection.type.charAt(0).toUpperCase() + connection.type.slice(1)}
                                                                            </span>
                                                                        </div>
                                                                        {index < userConnections.length - 1 && (
                                                                            <Separator className="my-1" />
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="px-4 py-3">
                                                            <p className="text-sm text-muted-foreground">{t("chat.agents.noAgentsConfigured")}</p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {t("chat.agents.configureAgentsHint")}
                                                            </p>
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </PopoverContent>
                                    </Popover>
                                    {/* {isPendingWebSearch ? (
                                        <Skeleton className="h-9 w-9 rounded-md border-none px-4 py-2 shadow-none" />
                                    ) : (
                                        webSearchData.webAgentEnabled && (
                                            <TooltipProvider>
                                                <Tooltip delayDuration={0}>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size={sendMessageParams.web ? "default" : "icon"}
                                                            onClick={() => setWeb(!sendMessageParams.web)}
                                                            className={cn(
                                                                sendMessageParams.web
                                                                    ? "bg-blue-100 px-2.5 text-blue-700 hover:bg-blue-200 hover:text-blue-700 dark:bg-blue-900/60 dark:text-blue-400 dark:hover:bg-blue-800/80 dark:hover:text-blue-300"
                                                                    : ""
                                                            )}
                                                        >
                                                            <Globe size={20} />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Web Agent</TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )
                                    )} */}
                                </div>
                                <div className="flex gap-2">
                                    {/* <Select value={modelId} onValueChange={setModelId}>
                                        {isLoading ? (
                                            <Skeleton className="h-9 w-24 border-none px-2 shadow-none" />
                                        ) : (
                                            <SelectTrigger className="h-9 border-none px-2 shadow-none">
                                                <SelectValue />
                                            </SelectTrigger>
                                        )}
                                        <SelectContent>
                                            {models?.map((model) => (
                                                <SelectItem key={model.id} value={model.id}>
                                                    {model.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select> */}
                                    <TooltipProvider>
                                        <Tooltip delayDuration={0}>
                                            <TooltipTrigger asChild>
                                                <AnimateIcon animateOnHover={!isSending}>
                                                    <Button
                                                        onClick={isSending ? handleStopGeneration : handleSendMessage}
                                                        size="icon"
                                                        className={cn(
                                                            "h-9 min-w-9 rounded-lg transition-all duration-200 ease-in-out",
                                                            isSending
                                                                ? "border border-border bg-muted shadow-sm hover:bg-muted/80 hover:shadow-md"
                                                                : "bg-primary shadow-md hover:bg-primary/90 hover:shadow-lg dark:bg-primary/80 dark:hover:bg-primary"
                                                        )}
                                                    >
                                                        {isSending ? (
                                                            <Square
                                                                size={16}
                                                                className="fill-current text-muted-foreground"
                                                            />
                                                        ) : (
                                                            <Send
                                                                size={18}
                                                                className="text-white"
                                                                animation="default"
                                                            />
                                                        )}
                                                    </Button>
                                                </AnimateIcon>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {isSending ? t("chat.stopGeneration") : t("chat.sendMessage")}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {((window as any).__BRANDING__?.pro_tip as string | undefined) && (
                    <div className="text-center text-xs text-gray-500">
                        {(window as any).__BRANDING__?.pro_tip as string}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatInputArea;
